import { Injectable } from '@angular/core';
import { BleClient, BleDevice } from '@capacitor-community/bluetooth-le';
import { AlertController, ToastController } from '@ionic/angular';
import { DevicesStoreFacade } from '@monorepo/core-data';
import { TBleModeInitDTO, TPairedDevice, TScannedInfoWithScanLockDetails } from '@monorepo/types';
import { InputAppErrorDisplay } from '@monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { BluetoothCommonService } from './bluetooth-common.service';
@Injectable({
  providedIn: 'root',
})
export class BleService {
  hasInitialized = false;
  serviceId = '';
  characteristicId = '';
  connectedDevice: BleDevice | null = null;
  availableDevices: BleDevice[] = [];
  pairedDevices: TPairedDevice[] = [];
  hasBluetoothEnabled = false;
  scannedInfoEmitter = new Subject<TScannedInfoWithScanLockDetails>();
  constructor(
    private devicesStoreFacade: DevicesStoreFacade,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private translate: TranslateService,
    private bluetoothCommonService: BluetoothCommonService,
    private errorDisplay: InputAppErrorDisplay,
  ) {}

  getScannedInfoEmitterSubscription() {
    return this.scannedInfoEmitter.asObservable();
  }

  async isLocationEnabled() {
    return await BleClient.isLocationEnabled();
  }

  async isBluetoothEnabled() {
    return await BleClient.isEnabled();
  }

  async hasLocationAndBluetoothEnabled(showLocationAlertInCaseDisabled = false) {
    const isLocationEnabled = await this.isLocationEnabled();
    const isBluetoothEnabled = await this.isBluetoothEnabled();
    if (!isLocationEnabled && showLocationAlertInCaseDisabled) this.showLocationAlert();
    return isBluetoothEnabled && isLocationEnabled;
  }

  getFilteredAvailableDevices(availableDevices: BleDevice[], pairedDeviceList: TPairedDevice[]) {
    return availableDevices.filter((deviceInfo) => {
      return !pairedDeviceList.find((pairedInfo) => pairedInfo.device.deviceId === deviceInfo.deviceId);
    });
  }

  async initBle(initDTO: TBleModeInitDTO) {
    const { serviceId, characteristicId } = initDTO;
    this.serviceId = serviceId;
    this.characteristicId = characteristicId;
    this.devicesStoreFacade.getPairedDeviceList().subscribe((pairedDeviceList) => {
      this.pairedDevices = pairedDeviceList;
      this.availableDevices = this.getFilteredAvailableDevices(this.availableDevices, pairedDeviceList);
    });
    await BleClient.initialize({ androidNeverForLocation: true });
    const isBluetoothEnabled = await BleClient.isEnabled();
    this.onBluetoothStateChange(isBluetoothEnabled);
    await BleClient.startEnabledNotifications((value) => {
      this.onBluetoothStateChange(value);
    });
  }

  async onBluetoothStateChange(newState: boolean) {
    if (newState !== this.hasBluetoothEnabled) {
      this.hasBluetoothEnabled = newState;
      if (!this.hasBluetoothEnabled) {
        this.availableDevices = [];
        this.connectedDevice = null;
      } else {
        await this.autoConnect();
      }
    }
  }

  async autoConnect(excludeDeviceId?: string) {
    const isLocationAndBluetoothEnabled = await this.hasLocationAndBluetoothEnabled(false);
    if (isLocationAndBluetoothEnabled && this.pairedDevices.length) {
      let lastPairedDevice: undefined | TPairedDevice = undefined;
      this.pairedDevices.forEach((pairedDevice) => {
        if (
          pairedDevice.device.deviceId !== excludeDeviceId &&
          (!lastPairedDevice || pairedDevice.lastConnectedAt > lastPairedDevice.lastConnectedAt)
        )
          lastPairedDevice = pairedDevice;
      });
      if (lastPairedDevice !== undefined) {
        await this.connectToDeviceAndStartNotifications((lastPairedDevice as TPairedDevice).device, true);
      }
    }
  }

  async updateAvailableDevicesList() {
    try {
      const res = await this.hasLocationAndBluetoothEnabled(true);
      if (res) {
        await this.errorDisplay.presentLoadingWithId(
          'UPDATE_DEVICE_LIST',
          this.translate.instant('ble_service.scanningDevices'),
        );
        const scannedDevicesInfo = await this.getDevicesList();
        await this.errorDisplay.dismissLoadingWithId('UPDATE_DEVICE_LIST');
        if (scannedDevicesInfo.status) {
          this.availableDevices = this.getFilteredAvailableDevices(scannedDevicesInfo.list, this.pairedDevices);
        } else {
          await this.showAlert(
            this.translate.instant('ble_service.stopScanningError'),
            this.translate.instant('input_app_common.error'),
          );
          this.availableDevices = [];
        }
      } else this.availableDevices = [];
    } catch (error) {
      this.availableDevices = [];
    }
  }

  // can be used to render the list of devices page bluetooth connection
  getDevicesList() {
    return new Promise<{ list: BleDevice[]; status: boolean }>((resolve) => {
      const devices: BleDevice[] = [];
      BleClient.requestLEScan({}, (device) => {
        if (device.device.name && !devices.find((deviceInfo) => deviceInfo.deviceId === device.device.deviceId))
          devices.push(device.device);
      });
      setTimeout(() => {
        BleClient.stopLEScan()
          .then(() => {
            resolve({ list: devices, status: true });
          })
          .catch((error) => {
            console.log('Cannot stop scanning after getting devices list', error);
            resolve({ list: devices, status: false });
          });
      }, 10000);
    });
  }

  async enableBluetooth() {
    try {
      const isEnabled = await BleClient.isEnabled();
      if (!isEnabled) {
        await BleClient.requestEnable();
      }
      return true;
    } catch (error) {
      console.log('Not able to enable bluetooth', error);
      this.hasBluetoothEnabled = false;
      return false;
    }
  }

  async connectToDeviceAndStartNotifications(bleDevice: BleDevice, isAutoConnect: boolean) {
    const res = await this.hasLocationAndBluetoothEnabled(!isAutoConnect);
    if (res) {
      await this.errorDisplay.presentLoadingWithId(
        'CONNECTING_TO_SCANNER',
        `${this.translate.instant('ble_service.connectingWithDevice')}: ${bleDevice.name}`,
      );
      const hasConnected = await this.disconnectAndConnectToDevice(bleDevice);
      if (hasConnected.status) {
        const isValid = await this.validateServiceIdAndCharacteristicId(
          bleDevice.deviceId,
          this.serviceId,
          this.characteristicId,
        );
        if (!isValid) {
          await this.errorDisplay.dismissLoadingWithId('CONNECTING_TO_SCANNER');
          await this.showAlert(
            `${this.translate.instant('ble_service.cantFindServiceId')}: ${
              bleDevice.name ? bleDevice.name : bleDevice.deviceId
            },
            ${this.translate.instant('ble_service.checkWithAdminTeam')}`,
            this.translate.instant('input_app_common.error'),
          );
        } else {
          await this.errorDisplay.dismissLoadingWithId('CONNECTING_TO_SCANNER');
          const deviceInfo: TPairedDevice = {
            device: bleDevice,
            lastConnectedAt: new Date().getTime(),
          };
          this.devicesStoreFacade.addBluetoothDeviceAction(deviceInfo);
          await this.showFailureToast(
            `${this.translate.instant('ble_service.connectedWithDevice')}: ${
              bleDevice.name ? bleDevice.name : bleDevice.deviceId
            }`,
          );
          this.connectedDevice = bleDevice;
          this.startNotifications(this.connectedDevice);
          this.devicesStoreFacade.updateConnectedDeviceInfo({
            connectionStatus: 'CONNECTED',
            connectionType: 'BLUETOOTH',
            deviceInfo,
          });
        }
      } else {
        await this.errorDisplay.dismissLoadingWithId('CONNECTING_TO_SCANNER');
        await this.showAlert(
          hasConnected.error
            ? hasConnected.error
            : `${this.translate.instant('ble_service.cantConnectWithDevice')}: ${
                bleDevice.name ? bleDevice.name : bleDevice.deviceId
              }, ${this.translate.instant('ble_service.pleaseScanForDevices')}`,
          this.translate.instant('input_app_common.error'),
        );
        this.devicesStoreFacade.updateConnectedDeviceInfo({
          connectionStatus: 'DISCONNECTED',
          connectionType: 'BLUETOOTH',
          deviceInfo: null,
        });
      }
    }
  }

  async disconnectAndConnectToDevice(bleDevice: BleDevice) {
    if (this.connectedDevice) {
      //This is to stop onDeviceDisconnect to trigger
      const previousConnectedDevice = this.connectedDevice;
      this.connectedDevice = null;
      const res = await this.disconnectDeviceManually(previousConnectedDevice.deviceId);
      if (!res.status) {
        this.connectedDevice = previousConnectedDevice;
        return {
          status: false,
          error: `${this.translate.instant('ble_service.disconnectingOlderDeviceError')}: ${
            previousConnectedDevice.name
          }`,
        };
      }
    }
    const res = await this.connectToDevice(bleDevice);
    return {
      status: res,
      error: !res ? `${this.translate.instant('ble_service.connectingWithDeviceError')}: ${bleDevice.name}` : undefined,
    };
  }

  async connectToDevice(bleDevice: BleDevice) {
    try {
      await BleClient.connect(
        bleDevice.deviceId,
        (deviceId: string) => {
          this.onDeviceDisconnect(deviceId);
        },
        { timeout: 15000 },
      );
      const deviceServices = await BleClient.getServices(bleDevice.deviceId);
      const connectedDevices = await BleClient.getConnectedDevices(
        deviceServices.map((deviceService) => deviceService.uuid),
      );

      if (connectedDevices.find((device) => device.deviceId === bleDevice.deviceId)) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.log('Error from getServices API or connectedDevices', error);
      return false;
    }
  }

  async onDeviceDisconnect(deviceId: string) {
    if (this.connectedDevice && this.connectedDevice.deviceId === deviceId) {
      const pastConnecteddevice = this.connectedDevice;
      this.connectedDevice = null;
      this.showAlert(
        `${this.translate.instant('ble_service.connectionLost')}: ${
          pastConnecteddevice.name ? pastConnecteddevice.name : pastConnecteddevice.deviceId
        }`,
        this.translate.instant('input_app_common.warning'),
      );
      this.devicesStoreFacade.updateConnectedDeviceInfo({
        connectionStatus: 'DISCONNECTED',
        connectionType: 'BLUETOOTH',
        deviceInfo: null,
      });
    }
  }

  async disconnectDeviceManually(deviceId: string) {
    try {
      await BleClient.disconnect(deviceId);
      return {
        status: true,
      };
    } catch (error) {
      console.log(error);
      return {
        status: false,
        error,
      };
    }
  }

  async onUserDisconnectDevice(deviceId: string) {
    if (this.connectedDevice && this.connectedDevice.deviceId === deviceId) {
      await this.errorDisplay.presentLoadingWithId(
        'USER_DISCONNECT_SCANNER',
        `${this.translate.instant('ble_service.disconnectingDevice')}: ${
          this.connectedDevice.name ? this.connectedDevice.name : this.connectedDevice.deviceId
        }`,
      );
      const res = await this.disconnectDeviceManually(deviceId);
      await this.errorDisplay.dismissLoadingWithId('USER_DISCONNECT_SCANNER');
      if (!res.status) {
        this.showAlert(
          `${this.translate.instant('ble_service.unableToDisconnectDevice')}: ${deviceId}, ${this.translate.instant(
            'ble_service.pleaseTryAgain',
          )}`,
          this.translate.instant('input_app_common.error'),
        );
      }
    } else {
      this.showAlert(
        `${this.translate.instant('ble_service.device')}: ${deviceId} ${this.translate.instant(
          'ble_service.isNotConnected',
        )}`,
        this.translate.instant('ble_service.warning'),
      );
      this.devicesStoreFacade.updateConnectedDeviceInfo({
        connectionStatus: 'DISCONNECTED',
        connectionType: 'BLUETOOTH',
        deviceInfo: null,
      });
    }
  }

  async unpairDevice(deviceId: string) {
    this.devicesStoreFacade.removeBluetoothDeviceAction(deviceId);
    if (this.connectedDevice && this.connectedDevice.deviceId === deviceId) {
      this.connectedDevice = null;
      await BleClient.disconnect(deviceId);
    }
  }

  async validateServiceIdAndCharacteristicId(deviceId: string, serviceId: string, characteristicId: string) {
    try {
      const bleServices = await BleClient.getServices(deviceId);
      return bleServices.some((serviceInfo) => {
        return (
          serviceInfo.uuid === serviceId &&
          serviceInfo.characteristics.some((characteristicInfo) => characteristicInfo.uuid === characteristicId)
        );
      });
    } catch (error) {
      return false;
    }
  }

  async showAlert(message: string, title: string) {
    const alert = await this.alertCtrl.create({
      header: title,
      subHeader: message,
      buttons: [
        {
          text: this.translate.instant('input_app_common.okay'),
        },
      ],
    });
    await alert.present();
  }

  async showFailureToast(message: string) {
    if (message) {
      const toast = await this.toastCtrl.create({
        message,
        duration: 3000,
        cssClass: 'failure-toast',
        position: 'top',
      });
      await toast.present();
    }
  }

  async showLocationAlert() {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('ble_service.warning'),
      subHeader: this.translate.instant('ble_service.pleaseEnableLocation'),
      backdropDismiss: false,
      buttons: [
        {
          text: this.translate.instant('input_app_common.okay'),
          handler: async () => {
            await BleClient.openLocationSettings();
          },
        },
      ],
    });
    await alert.present();
  }

  async startNotifications(bleDevice: BleDevice) {
    let scanStarted = false;
    let dataString: DataView[] = [];
    await BleClient.startNotifications(bleDevice.deviceId, this.serviceId, this.characteristicId, (value) => {
      dataString.push(value);
      if (!scanStarted) {
        scanStarted = true;
        setTimeout(() => {
          this.emitScannedData(dataString);
          scanStarted = false;
          dataString = [];
        }, 500);
      }
    });
  }

  emitScannedData(exportedPrivateKey: DataView[]) {
    let byteString = '';
    exportedPrivateKey.forEach((dataBuff) => {
      const byteArray = new Uint8Array(dataBuff.buffer);
      for (let i = 0; i < byteArray.byteLength; i++) {
        byteString += String.fromCodePoint(byteArray[i]);
      }
    });
    // user can scan globaly also to search tag history, if lock details source is SEARCH_TAG_HISTORY or isLocked = true ignore emitted scanner data  wherever you have subscribed this
    this.scannedInfoEmitter.next({ byteString, lockDetails: this.bluetoothCommonService.getScanLockDetails() });
    console.log('Scanned String', byteString);
  }
}
